import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  Badge,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { 
  Timeline, 
  TimelineItem, 
  TimelineSeparator, 
  TimelineDot, 
  TimelineContent,
  TimelineOppositeContent,
  TimelineConnector 
} from '@mui/lab';
import {
  Add,
  Notifications,
  NotificationsActive,
  Schedule,
  CheckCircle,
  Warning,
  Error,
  Delete,
  Edit,
  CalendarMonth,
  Description,
  Person,
  Work,
  AccountBalance,
  Flight,
  FolderOpen,
  Email,
  Sms,
  AlarmOn,
  Today,
  DateRange,
  Assignment
} from '@mui/icons-material';
import { Timeline as MuiTimeline } from '@mui/lab';
import api from '../services/api';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Reminders = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [createDialog, setCreateDialog] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [reminderForm, setReminderForm] = useState({
    reminder_type: 'document_expiry',
    category: 'documents',
    priority: 'medium',
    reminder_date: '',
    reminder_time: '09:00',
    reminder_message: '',
    related_employee_id: '',
    related_contract_id: '',
    related_document_id: '',
    related_posting_id: '',
    recurrence_type: 'none',
    recurrence_interval: 1,
    email_notification: true,
    sms_notification: false,
    system_notification: true,
    status: 'pending'
  });

  const [employees, setEmployees] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);

  // Reminder categories with icons and colors
  const categories = {
    'documents': { name: 'Documents', icon: <FolderOpen />, color: 'primary' },
    'contracts': { name: 'Contracts', icon: <Description />, color: 'success' },
    'sodra': { name: 'SODRA', icon: <AccountBalance />, color: 'warning' },
    'postings': { name: 'EU Postings', icon: <Flight />, color: 'info' },
    'employees': { name: 'Employees', icon: <Person />, color: 'secondary' },
    'compliance': { name: 'Compliance', icon: <Assignment />, color: 'error' },
    'other': { name: 'Other', icon: <Notifications />, color: 'default' }
  };

  // Reminder types
  const reminderTypes = {
    'document_expiry': 'Document Expiry',
    'contract_expiry': 'Contract Expiry',
    'sodra_deadline': 'SODRA Filing Deadline',
    'a1_expiry': 'A1 Form Expiry',
    'posting_end': 'Posting End Date',
    'work_permit_expiry': 'Work Permit Expiry',
    'birthday': 'Employee Birthday',
    'probation_end': 'Probation Period End',
    'custom': 'Custom Reminder'
  };

  // Priority levels
  const priorities = {
    'low': { label: 'Low', color: 'default' },
    'medium': { label: 'Medium', color: 'warning' },
    'high': { label: 'High', color: 'error' },
    'urgent': { label: 'Urgent', color: 'error' }
  };

  useEffect(() => {
    fetchReminders();
    fetchEmployees();
    fetchContracts();
    generateUpcomingEvents();
  }, []);

  const fetchReminders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/reminders');
      setReminders(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching reminders:', error);
      setError('Failed to load reminders');
      setReminders([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.filter(emp => emp.status === 'active'));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await api.get('/contracts');
      setContracts(response.data);
    } catch (error) {
      console.error('Error fetching contracts:', error);
    }
  };

  const generateUpcomingEvents = async () => {
    try {
      // This would fetch from various endpoints to compile upcoming events
      const events = [
        {
          id: 1,
          type: 'document_expiry',
          title: 'Passport Expiry - John Doe',
          date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
          priority: 'high',
          category: 'documents'
        },
        {
          id: 2,
          type: 'sodra_deadline',
          title: 'SAM Report Due',
          date: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 15),
          priority: 'urgent',
          category: 'sodra'
        },
        {
          id: 3,
          type: 'contract_expiry',
          title: 'Contract Ending - Jane Smith',
          date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          priority: 'medium',
          category: 'contracts'
        },
        {
          id: 4,
          type: 'posting_end',
          title: 'Germany Posting Ends - Mike Johnson',
          date: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
          priority: 'medium',
          category: 'postings'
        }
      ];
      setUpcomingEvents(events.sort((a, b) => a.date - b.date));
    } catch (error) {
      console.error('Error generating events:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString, timeString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (timeString) {
      const [hours, minutes] = timeString.split(':');
      date.setHours(hours, minutes);
    }
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntil = (date) => {
    const today = new Date();
    const targetDate = new Date(date);
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    return `In ${diffDays} days`;
  };

  const getReminderStatus = (reminder) => {
    if (reminder.status === 'completed') {
      return { label: 'Completed', color: 'success', icon: <CheckCircle /> };
    }
    if (reminder.status === 'acknowledged') {
      return { label: 'Acknowledged', color: 'info', icon: <CheckCircle /> };
    }
    if (reminder.status === 'cancelled') {
      return { label: 'Cancelled', color: 'default', icon: <Delete /> };
    }
    
    const reminderDate = new Date(reminder.reminder_date);
    const today = new Date();
    
    if (reminderDate < today) {
      return { label: 'Overdue', color: 'error', icon: <Warning /> };
    }
    
    return { label: 'Pending', color: 'warning', icon: <Schedule /> };
  };

  const handleCreateReminder = async () => {
    try {
      await api.post('/reminders/create', reminderForm);
      setSuccess('Reminder created successfully!');
      setCreateDialog(false);
      fetchReminders();
      resetForm();
    } catch (error) {
      console.error('Error creating reminder:', error);
      setError('Failed to create reminder');
    }
  };

  const handleAcknowledge = async (reminderId) => {
    try {
      await api.post(`/reminders/${reminderId}/acknowledge`);
      fetchReminders();
      setSuccess('Reminder acknowledged');
    } catch (error) {
      console.error('Error acknowledging reminder:', error);
      setError('Failed to acknowledge reminder');
    }
  };

  const handleDelete = async (reminderId) => {
    if (window.confirm('Are you sure you want to delete this reminder?')) {
      try {
        await api.delete(`/reminders/${reminderId}`);
        fetchReminders();
        setSuccess('Reminder deleted');
      } catch (error) {
        console.error('Error deleting reminder:', error);
        setError('Failed to delete reminder');
      }
    }
  };

  const resetForm = () => {
    setReminderForm({
      reminder_type: 'document_expiry',
      category: 'documents',
      priority: 'medium',
      reminder_date: '',
      reminder_time: '09:00',
      reminder_message: '',
      related_employee_id: '',
      related_contract_id: '',
      related_document_id: '',
      related_posting_id: '',
      recurrence_type: 'none',
      recurrence_interval: 1,
      email_notification: true,
      sms_notification: false,
      system_notification: true,
      status: 'pending'
    });
  };

  // Filter reminders
  const todayReminders = reminders.filter(r => {
    const reminderDate = new Date(r.reminder_date);
    const today = new Date();
    return reminderDate.toDateString() === today.toDateString() && r.status === 'pending';
  });

  const upcomingReminders = reminders.filter(r => {
    const reminderDate = new Date(r.reminder_date);
    const today = new Date();
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
    return reminderDate > today && reminderDate <= nextWeek && r.status === 'pending';
  });

  const overdueReminders = reminders.filter(r => {
    const reminderDate = new Date(r.reminder_date);
    const today = new Date();
    return reminderDate < today && r.status === 'pending';
  });

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Reminders & Notifications
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setCreateDialog(true)}
        >
          Create Reminder
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Badge badgeContent={todayReminders.length} color="error">
                  <Today color="primary" />
                </Badge>
                <Box>
                  <Typography variant="h6">{todayReminders.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Today's Reminders
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DateRange color="info" />
                <Box>
                  <Typography variant="h6">{upcomingReminders.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    This Week
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Warning color="error" />
                <Box>
                  <Typography variant="h6">{overdueReminders.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Overdue
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NotificationsActive color="success" />
                <Box>
                  <Typography variant="h6">{reminders.length}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Today" />
          <Tab label="Upcoming" />
          <Tab label="Timeline" />
          <Tab label="By Category" />
          <Tab label="Settings" />
        </Tabs>
      </Paper>

      {/* Today Tab */}
      <TabPanel value={tabValue} index={0}>
        {todayReminders.length === 0 ? (
          <Alert severity="info">No reminders for today</Alert>
        ) : (
          <List>
            {todayReminders.map((reminder) => {
              const category = categories[reminder.category] || categories.other;
              const status = getReminderStatus(reminder);
              const priority = priorities[reminder.priority];
              
              return (
                <Paper key={reminder.id} sx={{ mb: 2, p: 2 }}>
                  <ListItem>
                    <ListItemIcon>
                      {category.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {reminder.reminder_message}
                          </Typography>
                          <Chip
                            label={priority.label}
                            color={priority.color}
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {reminderTypes[reminder.reminder_type]} • {formatDateTime(reminder.reminder_date, reminder.reminder_time)}
                          </Typography>
                          {reminder.related_employee_name && (
                            <Typography variant="caption" display="block">
                              Employee: {reminder.related_employee_name}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => handleAcknowledge(reminder.id)}
                        sx={{ mr: 1 }}
                      >
                        Complete
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(reminder.id)}
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Box sx={{ px: 2, display: 'flex', gap: 1 }}>
                    {reminder.email_notification && (
                      <Chip icon={<Email />} label="Email" size="small" />
                    )}
                    {reminder.sms_notification && (
                      <Chip icon={<Sms />} label="SMS" size="small" />
                    )}
                    {reminder.system_notification && (
                      <Chip icon={<Notifications />} label="System" size="small" />
                    )}
                  </Box>
                </Paper>
              );
            })}
          </List>
        )}
      </TabPanel>

      {/* Upcoming Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              This Week's Reminders
            </Typography>
            {upcomingReminders.length === 0 ? (
              <Alert severity="info">No upcoming reminders this week</Alert>
            ) : (
              <List>
                {upcomingReminders.map((reminder) => {
                  const category = categories[reminder.category] || categories.other;
                  const priority = priorities[reminder.priority];
                  
                  return (
                    <Card key={reminder.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {category.icon}
                            <Box>
                              <Typography variant="subtitle2">
                                {reminder.reminder_message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getDaysUntil(reminder.reminder_date)} • {formatDate(reminder.reminder_date)}
                              </Typography>
                            </Box>
                          </Box>
                          <Chip
                            label={priority.label}
                            color={priority.color}
                            size="small"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </List>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Overdue Reminders
            </Typography>
            {overdueReminders.length === 0 ? (
              <Alert severity="success">No overdue reminders</Alert>
            ) : (
              <List>
                {overdueReminders.map((reminder) => {
                  const category = categories[reminder.category] || categories.other;
                  
                  return (
                    <Card key={reminder.id} sx={{ mb: 2, bgcolor: 'error.lighter' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Warning color="error" />
                            <Box>
                              <Typography variant="subtitle2">
                                {reminder.reminder_message}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {getDaysUntil(reminder.reminder_date)}
                              </Typography>
                            </Box>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => handleAcknowledge(reminder.id)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </List>
            )}
          </Grid>
        </Grid>
      </TabPanel>

      {/* Timeline Tab */}
      <TabPanel value={tabValue} index={2}>
        <Typography variant="h6" gutterBottom>
          Upcoming Events Timeline
        </Typography>
        <MuiTimeline position="alternate">
          {upcomingEvents.map((event, index) => {
            const category = categories[event.category];
            const priority = priorities[event.priority];
            
            return (
              <TimelineItem key={event.id}>
                <TimelineOppositeContent color="text.secondary">
                  {formatDate(event.date)}
                  <Typography variant="caption" display="block">
                    {getDaysUntil(event.date)}
                  </Typography>
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineDot color={category.color}>
                    {category.icon}
                  </TimelineDot>
                  {index < upcomingEvents.length - 1 && <TimelineConnector />}
                </TimelineSeparator>
                <TimelineContent>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="subtitle2">{event.title}</Typography>
                    <Chip
                      label={priority.label}
                      color={priority.color}
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </MuiTimeline>
      </TabPanel>

      {/* By Category Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          {Object.entries(categories).map(([key, category]) => {
            const categoryReminders = reminders.filter(r => r.category === key && r.status === 'pending');
            
            return (
              <Grid item xs={12} md={4} key={key}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {category.icon}
                      <Box>
                        <Typography variant="h6">{categoryReminders.length}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {category.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {categoryReminders.slice(0, 3).map(reminder => (
                      <Typography key={reminder.id} variant="body2" sx={{ mt: 1 }}>
                        • {reminder.reminder_message.substring(0, 30)}...
                      </Typography>
                    ))}
                    {categoryReminders.length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        +{categoryReminders.length - 3} more
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </TabPanel>

      {/* Settings Tab */}
      <TabPanel value={tabValue} index={4}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Preferences
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Email Notifications"
                      secondary="Receive reminders via email"
                    />
                    <ListItemSecondaryAction>
                      <Switch defaultChecked />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="SMS Notifications"
                      secondary="Receive urgent reminders via SMS"
                    />
                    <ListItemSecondaryAction>
                      <Switch />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="System Notifications"
                      secondary="Show in-app notifications"
                    />
                    <ListItemSecondaryAction>
                      <Switch defaultChecked />
                    </ListItemSecondaryAction>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Default Reminder Times
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Document Expiry"
                      secondary="30 days before expiry"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Contract Expiry"
                      secondary="60 days before expiry"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="SODRA Deadline"
                      secondary="5 days before deadline"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="A1 Form Expiry"
                      secondary="90 days before expiry"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Create Reminder Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Reminder</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Reminder Type"
                value={reminderForm.reminder_type}
                onChange={(e) => setReminderForm({ ...reminderForm, reminder_type: e.target.value })}
              >
                {Object.entries(reminderTypes).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Category"
                value={reminderForm.category}
                onChange={(e) => setReminderForm({ ...reminderForm, category: e.target.value })}
              >
                {Object.entries(categories).map(([key, cat]) => (
                  <MenuItem key={key} value={key}>{cat.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Priority"
                value={reminderForm.priority}
                onChange={(e) => setReminderForm({ ...reminderForm, priority: e.target.value })}
              >
                {Object.entries(priorities).map(([key, pri]) => (
                  <MenuItem key={key} value={key}>{pri.label}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Date"
                type="date"
                value={reminderForm.reminder_date}
                onChange={(e) => setReminderForm({ ...reminderForm, reminder_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Time"
                type="time"
                value={reminderForm.reminder_time}
                onChange={(e) => setReminderForm({ ...reminderForm, reminder_time: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reminder Message"
                value={reminderForm.reminder_message}
                onChange={(e) => setReminderForm({ ...reminderForm, reminder_message: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Related Employee (Optional)"
                value={reminderForm.related_employee_id}
                onChange={(e) => setReminderForm({ ...reminderForm, related_employee_id: e.target.value })}
              >
                <MenuItem value="">None</MenuItem>
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Recurrence"
                value={reminderForm.recurrence_type}
                onChange={(e) => setReminderForm({ ...reminderForm, recurrence_type: e.target.value })}
              >
                <MenuItem value="none">No Recurrence</MenuItem>
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
                <MenuItem value="yearly">Yearly</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Notification Methods
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={reminderForm.email_notification}
                    onChange={(e) => setReminderForm({ ...reminderForm, email_notification: e.target.checked })}
                  />
                }
                label="Email"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={reminderForm.sms_notification}
                    onChange={(e) => setReminderForm({ ...reminderForm, sms_notification: e.target.checked })}
                  />
                }
                label="SMS"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={reminderForm.system_notification}
                    onChange={(e) => setReminderForm({ ...reminderForm, system_notification: e.target.checked })}
                  />
                }
                label="System"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateReminder} variant="contained">
            Create Reminder
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Reminders;
